import React, { useEffect, useState } from 'react'
import MDEditor from '@uiw/react-md-editor'
import { cn } from '@/lib/utils'
import '@/styles/markdown-editor.css'

/**
 * MarkdownEditor - Full-featured Markdown editor with toolbar and live preview
 *
 * **USE WHEN:** You need users to edit markdown content with formatting help
 *
 * **LIBRARY:** @uiw/react-md-editor (full WYSIWYG-like experience)
 *
 * **FEATURES:**
 * - Full toolbar with formatting buttons (bold, italic, lists, etc.)
 * - Live preview or split-view editing
 * - Syntax highlighting
 * - Dark mode support
 *
 * **EXAMPLES:**
 * - FAQ answer editing in faq-editor.tsx
 * - System prompt editing
 * - Any long-form markdown content
 *
 * **DON'T USE FOR:**
 * - Read-only display → use MarkdownRenderer instead
 * - Compact inline preview → use MarkdownPreview instead
 */
interface MarkdownEditorProps {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  className?: string
  height?: number
  preview?: 'live' | 'edit' | 'preview'
  hideToolbar?: boolean
  readOnly?: boolean
}

export function MarkdownEditor({
  value = '',
  onChange,
  placeholder = 'Geben Sie hier Ihren Text ein...\n\n**Markdown** wird unterstützt:\n- **Fett**: `**text**`\n- *Kursiv*: `*text*`\n- Listen: `- item`\n- Links: `[text](url)`\n- Code: `` `code` ``',
  className,
  height = 400,
  preview = 'live',
  hideToolbar = false,
  readOnly = false
}: MarkdownEditorProps) {
  // Detect dark mode from the root element
  const [colorMode, setColorMode] = useState<'light' | 'dark'>('light')

  useEffect(() => {
    // Initial check
    const isDark = document.documentElement.classList.contains('dark')
    setColorMode(isDark ? 'dark' : 'light')

    // Watch for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const isDark = document.documentElement.classList.contains('dark')
          setColorMode(isDark ? 'dark' : 'light')
        }
      })
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })

    return () => observer.disconnect()
  }, [])

  return (
    <div className={cn('w-full markdown-editor-clean', className)} data-color-mode={colorMode}>
      <MDEditor
        value={value}
        onChange={(val) => onChange?.(val || '')}
        preview={preview}
        height={height}
        hideToolbar={hideToolbar}
        textareaProps={{
          placeholder,
          readOnly
        }}
        previewOptions={{
          components: {
            code: ({inline, className, children, ...props}: any) => {
              return (
                <code
                  className={cn(
                    inline
                      ? 'px-1 py-0.5 bg-gray-100 text-gray-900 rounded text-sm font-mono'
                      : 'block p-3 bg-gray-100 text-gray-900 rounded text-sm font-mono overflow-x-auto',
                    className
                  )}
                  {...props}
                >
                  {children}
                </code>
              )
            }
          }
        }}
      />

    </div>
  )
}

// Minimal Markdown Editor ohne Toolbar für kleinere Eingaben
export function SimpleMarkdownEditor(props: Omit<MarkdownEditorProps, 'hideToolbar' | 'preview'>) {
  return (
    <MarkdownEditor
      {...props}
      hideToolbar={true}
      preview="edit"
      height={props.height || 150}
    />
  )
}